import { PlatformError } from "./client";

/* What a sign-in attempt can end in, beyond "it worked".

   Every method can be refused for the same few reasons, but they arrive two
   ways: the JSON methods (magic link, SIWE/SIWS, Telegram) answer a 403 whose
   `details.reason` says why, and the OAuth popups (Google, X) cannot answer
   JSON at all — the API's own page posts a message to the opener instead
   (`createAuthWindowResponse` on the platform). Both are folded into one
   `SignInRefusal` here, so the dialog has one set of words for each. */

export type AccountUnavailable =
  | { reason: "ACCOUNT_CLOSED" }
  | { reason: "ACCOUNT_SELF_EXCLUDED"; endsAt: string };

export type SignInRefusal =
  | { kind: "geoblocked" }
  | { kind: "account-unavailable"; account: AccountUnavailable }
  /* A 403 with no reason: the platform does not trust this host, or the
     method is not configured for the site. */
  | { kind: "not-allowed" }
  | { kind: "interrupted" }
  | { kind: "failed" };

const reasonOf = (details: unknown) =>
  typeof details === "object" && details !== null ? (details as { reason?: unknown; endsAt?: unknown }) : null;

export const accountUnavailableFrom = (error: unknown): AccountUnavailable | null => {
  if (!(error instanceof PlatformError) || error.status !== 403) {
    return null;
  }

  const details = reasonOf(error.details);

  if (details?.reason === "ACCOUNT_CLOSED") {
    return { reason: "ACCOUNT_CLOSED" };
  }

  if (details?.reason === "ACCOUNT_SELF_EXCLUDED" && typeof details.endsAt === "string") {
    return { reason: "ACCOUNT_SELF_EXCLUDED", endsAt: details.endsAt };
  }

  return null;
};

export const isGeoblockedError = (error: unknown) =>
  error instanceof PlatformError &&
  error.status === 403 &&
  reasonOf(error.details)?.reason === "SITE_GEOBLOCKED";

/* The messages the API's auth window posts to `window.opener`. Anything else
   arriving on the message channel — from a wallet extension, a game frame, or
   a page pretending to be the API — is not one of these and is ignored. */
export type AuthWindowMessage =
  | { type: "SIGNIN_COMPLETE" }
  | { type: "SIGNIN_ERROR" }
  | { type: "GEOBLOCKED" }
  | { type: "REFERRAL_CODE_CONFLICT" }
  | { type: "ACCOUNT_CLOSED" }
  | { type: "ACCOUNT_SELF_EXCLUDED"; endsAt: string };

const PLAIN_TYPES = new Set(["SIGNIN_COMPLETE", "SIGNIN_ERROR", "GEOBLOCKED", "REFERRAL_CODE_CONFLICT", "ACCOUNT_CLOSED"]);

export const authWindowMessage = (data: unknown): AuthWindowMessage | null => {
  if (typeof data !== "object" || data === null) {
    return null;
  }

  const { type, endsAt } = data as { type?: unknown; endsAt?: unknown };

  if (type === "ACCOUNT_SELF_EXCLUDED") {
    return typeof endsAt === "string" ? { type, endsAt } : null;
  }

  return typeof type === "string" && PLAIN_TYPES.has(type) ? ({ type } as AuthWindowMessage) : null;
};

/* The refusal an auth-window message stands for; null for the two that are
   not refusals (complete, and the conflict the caller retries). */
export const refusalFromAuthWindow = (message: AuthWindowMessage): SignInRefusal | null => {
  switch (message.type) {
    case "SIGNIN_COMPLETE":
    case "REFERRAL_CODE_CONFLICT":
      return null;
    case "GEOBLOCKED":
      return { kind: "geoblocked" };
    case "ACCOUNT_CLOSED":
      return { kind: "account-unavailable", account: { reason: "ACCOUNT_CLOSED" } };
    case "ACCOUNT_SELF_EXCLUDED":
      return {
        kind: "account-unavailable",
        account: { reason: "ACCOUNT_SELF_EXCLUDED", endsAt: message.endsAt },
      };
    default:
      return { kind: "failed" };
  }
};

/* The same, for a JSON sign-in that threw. */
export const refusalFromError = (error: unknown): SignInRefusal => {
  if (isGeoblockedError(error)) {
    return { kind: "geoblocked" };
  }

  const account = accountUnavailableFrom(error);

  if (account) {
    return { kind: "account-unavailable", account };
  }

  if (error instanceof PlatformError && error.kind === "forbidden") {
    return { kind: "not-allowed" };
  }

  return { kind: "failed" };
};

/* The player-facing sentence for an unavailable account, after the
   whitelabel's: the end date only when it is a real one in the next few
   years — a permanent exclusion is stored as a far-future date and is not
   worth printing. */
export const describeAccountUnavailable = (account: AccountUnavailable, now = new Date()) => {
  if (account.reason === "ACCOUNT_CLOSED") {
    return "Your account is closed and can't be used to sign in.";
  }

  const ends = new Date(account.endsAt);
  const horizon = new Date(now);
  horizon.setUTCFullYear(horizon.getUTCFullYear() + 5);

  if (Number.isNaN(ends.getTime()) || ends <= now || ends > horizon) {
    return "Your account is self-excluded.";
  }

  const date = ends.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });

  return `Your account is self-excluded. The exclusion ends on ${date}.`;
};
